"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { ArrowRight, ArrowUpRight } from "lucide-react";

interface SubTreatment {
      id: number;
      title: string;
      slug: string;
}

interface TreatmentCategory {
      id: number;
      documentId: string;
      title: string;
      slug: string;
      icon?: {
            url: string;
      };
      heroImage?: {
            url: string;
      };
      treatments: SubTreatment[];
}

interface TreatmentContent {
      badge: string;
      title: string;
      description: string;
}

interface TreatmentsSectionProps {
      items: TreatmentCategory[];
      content: TreatmentContent;
}

export default function TreatmentsSection({ items, content }: TreatmentsSectionProps) {
      const t = useTranslations("common");
      if (!items || items.length === 0) return null;

      return (
            <section className="relative py-24 bg-[#F9F9F9] overflow-hidden">
                  {/* Arka Plan Dekoratif Blur */}
                  <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                        <div className="absolute top-[10%] right-[-5%] w-[800px] h-[800px] bg-[#A68F72] rounded-full blur-[140px] opacity-[0.03]" />
                        <div className="absolute bottom-[0%] left-[-10%] w-[600px] h-[600px] bg-[#D4C5A9] rounded-full blur-[120px] opacity-[0.04]" />
                  </div>

                  <div className="container mx-auto px-4 md:px-8 relative z-10">
                        {/* Header Kısmı */}
                        <div className="max-w-3xl mb-16">
                              {content?.badge && (
                                    <span className="inline-block py-1.5 px-4 rounded-full bg-white text-[#A68F72] text-[11px] font-bold tracking-widest uppercase mb-5 shadow-sm border border-[#A68F72]/10">
                                          {content.badge}
                                    </span>
                              )}
                              <h2 className="text-3xl md:text-5xl font-bold text-[#1a1a1a] mb-6 tracking-tight leading-[1.15]">
                                    {content?.title}
                              </h2>
                              <p className="text-gray-500 text-lg leading-relaxed max-w-2xl font-light">
                                    {content?.description}
                              </p>
                        </div>

                        {/* Kartlar Grid Yapısı */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                              {items.map((category) => (
                                    <div
                                          key={category.id}
                                          className="group flex flex-col bg-white rounded-2xl shadow-[0_10px_30px_-10px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_50px_-10px_rgba(166,143,114,0.15)] transition-all duration-500 overflow-hidden border border-gray-100/50"
                                    >
                                          {/* 1. Görsel Alanı (Hero Image) */}
                                          <Link href={`/${category.slug}` as any} className="relative w-full h-56 bg-gray-100 overflow-hidden block">
                                                {category.heroImage?.url ? (
                                                      <Image
                                                            src={`${process.env.NEXT_PUBLIC_API_URL}${category.heroImage.url}`}
                                                            alt={category.title}
                                                            fill
                                                            className="object-cover transition-transform duration-700 group-hover:scale-105"
                                                      />
                                                ) : (
                                                      <div className="absolute inset-0 bg-gradient-to-br from-[#EAE5DC] to-[#F5F2ED]" />
                                                )}

                                                {/* Hover Efekti */}
                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-500" />

                                                {/* Sağ Üst Köşe Link İkonu */}
                                                <div className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur rounded-full flex items-center justify-center text-gray-800 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 hover:bg-[#A68F72] hover:text-white z-20">
                                                      <ArrowUpRight size={18} />
                                                </div>
                                          </Link>

                                          {/* 2. İçerik Alanı */}
                                          <div className="p-8 pt-6 flex flex-col grow">

                                                {/* Başlık ve İkon Yan Yana (Revize Edilen Kısım) */}
                                                <div className="flex items-start gap-4 mb-6">
                                                      <div className="shrink-0 w-12 h-12 bg-[#A68F72]/10 rounded-xl flex items-center justify-center text-[#A68F72]">
                                                            {category.icon?.url ? (
                                                                  <Image
                                                                        src={`${process.env.NEXT_PUBLIC_API_URL}${category.icon.url}`}
                                                                        alt="icon"
                                                                        width={24}
                                                                        height={24}
                                                                        className="object-contain"
                                                                  />
                                                            ) : (
                                                                  <div className="w-5 h-5 bg-[#A68F72]/20 rounded-full" />
                                                            )}
                                                      </div>

                                                      <Link href={`/${category.slug}` as any} className="group/title pt-1">
                                                            <h3 className="text-xl font-bold text-[#1a1a1a] leading-tight group-hover/title:text-[#A68F72] transition-colors">
                                                                  {category.title}
                                                            </h3>
                                                      </Link>
                                                </div>

                                                {/* Alt Menüler - Font Boyutu Arttırıldı */}
                                                <ul className="space-y-3.5 mt-auto mb-6">
                                                      {category.treatments?.slice(0, 5).map((treatment) => (
                                                            <li key={treatment.id}>
                                                                  <Link
                                                                        href={`/${treatment.slug}` as any}
                                                                        className="flex items-center group/item w-full"
                                                                  >
                                                                        <span className="w-1.5 h-1.5 rounded-full bg-[#E5E5E5] group-hover/item:bg-[#A68F72] transition-colors mr-3 shrink-0" />
                                                                        <span className="text-gray-600 text-base font-medium group-hover/item:text-[#1a1a1a] transition-colors truncate">
                                                                              {treatment.title}
                                                                        </span>
                                                                  </Link>
                                                            </li>
                                                      ))}
                                                </ul>

                                                {/* Detay Linki - Artık Çalışıyor */}
                                                <Link
                                                      href={`/${category.slug}` as any}
                                                      className="mt-auto pt-6 border-t border-gray-100 flex items-center justify-between group/link hover:bg-gray-50/50 -mx-8 px-8 -mb-2 py-2 transition-colors"
                                                >
                                                      <span className="text-xs font-bold text-gray-500 uppercase tracking-widest group-hover/link:text-[#A68F72] transition-colors">
                                                            {t("viewDetails")}
                                                      </span>
                                                      <ArrowRight size={16} className="text-[#A68F72] group-hover/link:translate-x-1 transition-transform" />
                                                </Link>
                                          </div>
                                    </div>
                              ))}
                        </div>
                  </div>
            </section>
      );
}